import { getFacilitiesOptionsQueryOptions } from '@/entities/facility/api/get-facilities-options.query';
import { useFacilityModeStore } from '@/entities/facility/model/facility-mode.store';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';

type NavigateToFacilityParams = {
  facilityId?: string | number;
  facilityName?: string;
};

export const useFacilityNavigation = () => {
  const navigate = useNavigate();
  const selectFacility = useFacilityModeStore((state) => state.selectFacility);
  const { data: facilitiesOptions } = useQuery(
    getFacilitiesOptionsQueryOptions
  );

  const isFacilityAccessible = useCallback(
    (facilityId: number | string) => {
      return facilitiesOptions?.some((f) => f.value === String(facilityId));
    },
    [facilitiesOptions]
  );

  const getFacilityByName = useCallback(
    (facilityName: string) => {
      return facilitiesOptions?.find((f) => f.label === facilityName);
    },
    [facilitiesOptions]
  );

  /**
   * Entering Single Mode (card #157 Part 2). Clicking a facility row, or a
   * facility name in a chart, now switches the GLOBAL mode and lands on the
   * unified Facilities page rather than routing to the per-facility page.
   */
  const navigateToFacility = useCallback(
    ({ facilityId, facilityName }: NavigateToFacilityParams) => {
      if (!facilityId && !facilityName) return;

      const computedFacilityId =
        facilityId ?? getFacilityByName(facilityName!)?.value;

      if (computedFacilityId) {
        selectFacility(String(computedFacilityId));
        navigate({ to: '/facilities' });
      }
    },
    [getFacilityByName, navigate, selectFacility]
  );

  const handleFacilityClick = useCallback(
    <T extends { facilityId?: number; id?: number | string }>(
      data: T[] | undefined,
      index: number
    ) => {
      const item = data?.[index];
      if (!item) return;
      const facilityId = item.facilityId ?? item.id;
      if (facilityId) {
        navigateToFacility({ facilityId });
      }
    },
    [navigateToFacility]
  );

  return {
    facilitiesOptions,
    isFacilityAccessible,
    getFacilityByName,
    navigateToFacility,
    handleFacilityClick,
  };
};
